import { Location } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, HostListener, Input, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { interval, Subscription } from "rxjs";
import { UiNotificationService } from "../../features/ui-notification/ui-notification.service";
import { environment } from "../../../environments/environment";
import { SideNavigatorService } from "../../services/side-navigator.service";
import { NetworkService } from "src/app/services/network.service";
import { AuthService } from "src/app/services/auth.service";
import { ApiUrlService } from "src/app/services/api-url.service";
import { Capacitor } from "@capacitor/core";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
})
export class HeaderComponent implements OnInit {
  intervalSubscription = null;
  updateSubscription = null;
  activatedSubscription = null;

  networkSubscription: Subscription | null = null;
  online = true;
  organizationLogoUrl: string;

  @Input() title: string;

  constructor(
    private location: Location,
    private router: Router,
    private updates: SwUpdate,
    public sideNavigatorService: SideNavigatorService,
    public notificationService: UiNotificationService,
    public networkService: NetworkService,
    private authService: AuthService,
    private http: HttpClient,
    private apiUrlService: ApiUrlService
  ) {
    if (updates.isEnabled) {
      this.intervalSubscription = interval(1000 * 60 * 60 * 24).subscribe(() =>
        updates.checkForUpdate().then(() => console.log("checking for updates"))
      );
    }

    this.networkSubscription = this.networkService
      .getOnlineStatus()
      .subscribe((isOnline) => {
        this.online = isOnline;
      });
  }

  ngOnInit(): void {
    this.updateSubscription = this.updates.versionUpdates.subscribe(
      (event: VersionReadyEvent) => {
        console.log(event);
        if (event.type === "VERSION_READY") {
          console.log("current version is", event.currentVersion);
          console.log("available version is", event.latestVersion);
          console.log("updating to latest available version");
          this.updates.activateUpdate().then(() => {
            this.notificationService.initUpdateNotificationPrompt(
              environment.timeStamp
            );
          });
        }
      }
    );
    this.updates.activateUpdate().then(
      (val) => console.log("new version installed: " + val),
      (err) => console.error(err)
    );

    this.loadOrganizationLogo();
  }

  async loadOrganizationLogo(): Promise<void> {
    try {
      const userInfo = await this.authService.getUserInfo();
      // Active org, not the legacy field a switch never touches - otherwise
      // a multi-org user who's switched sees their original org's logo.
      const organizationId =
        userInfo?.active_organization_id || userInfo?.organization_id;
      if (!organizationId) {
        this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
        return;
      }

      const localStorageKey = `organizationLogo_${organizationId}`;
      const cachedLogo = localStorage.getItem(localStorageKey);
      if (cachedLogo) {
        this.organizationLogoUrl = cachedLogo;
      }

      const apiUrl = this.apiUrlService.getApiUrl();
      const logoUrl = `${apiUrl}/api/organizations/logo?organization_id=${organizationId}`;
      this.http.get(logoUrl).subscribe(
        (response: any) => {
          if (response && response.logo) {
            const logoDataUrl = `data:image/png;base64,${response.logo}`;
            this.organizationLogoUrl = logoDataUrl;
            if (cachedLogo !== logoDataUrl) {
              localStorage.setItem(localStorageKey, logoDataUrl);
            }
          } else if (!cachedLogo) {
            this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
          }
        },
        (error) => {
          console.error("Error loading organization logo for header:", error);
          if (!cachedLogo) {
            this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
          }
        }
      );
    } catch (error) {
      console.error("Error fetching user info for header logo:", error);
      this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
    }
  }

  // Feature name while inside a feature, brand name on home
  get displayTitle(): string {
    return !this.title || this.title === "MedicNow" ? "Medic Now" : this.title;
  }

  // ── Viewport diagnostics (press and hold the title) ──────────────────────
  // Temporary aid for the intermittent iOS "app shifted down under the
  // status bar" issue: shows what the WebView reports so it can be captured
  // in a screenshot while the problem is happening.
  diagnostics: string | null = null;
  private diagnosticsTimer: any = null;

  startDiagnosticsPress(): void {
    this.cancelDiagnosticsPress();
    this.diagnosticsTimer = setTimeout(() => {
      this.diagnostics = this.collectViewportDiagnostics();
    }, 900);
  }

  cancelDiagnosticsPress(): void {
    if (this.diagnosticsTimer) {
      clearTimeout(this.diagnosticsTimer);
      this.diagnosticsTimer = null;
    }
  }

  private collectViewportDiagnostics(): string {
    // Measure CSS-only values (env() insets, viewport units) with a probe
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;" +
      "padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom);" +
      "width:1px;height:100svh;";
    document.body.appendChild(probe);
    const probeStyle = getComputedStyle(probe);
    const envTop = probeStyle.paddingTop;
    const envBottom = probeStyle.paddingBottom;
    const svh = probe.getBoundingClientRect().height;
    probe.style.height = "100lvh";
    const lvh = probe.getBoundingClientRect().height;
    probe.style.height = "100dvh";
    const dvh = probe.getBoundingClientRect().height;
    probe.remove();

    const rect = (selector: string) => {
      const el = document.querySelector(selector);
      if (!el) return "n/a";
      const r = el.getBoundingClientRect();
      return `top ${Math.round(r.top)} bottom ${Math.round(
        r.bottom
      )} h ${Math.round(r.height)}`;
    };
    const rootStyle = getComputedStyle(document.documentElement);
    const vv = window.visualViewport;
    const nav: any = navigator;
    const round = (n: number) => Math.round(n * 10) / 10;

    return [
      `platform: ${Capacitor.getPlatform()}`,
      `standalone: ${!!nav.standalone} / display-mode standalone: ${
        window.matchMedia("(display-mode: standalone)").matches
      }`,
      `build: ${environment.timeStamp}`,
      `ua: ${navigator.userAgent}`,
      ``,
      `screen: ${screen.width} x ${screen.height}  dpr ${window.devicePixelRatio}`,
      `inner: ${window.innerWidth} x ${window.innerHeight}`,
      `outer: ${window.outerWidth} x ${window.outerHeight}`,
      `docElement client: ${document.documentElement.clientWidth} x ${document.documentElement.clientHeight}`,
      `visualViewport: ${
        vv
          ? `${round(vv.width)} x ${round(vv.height)} offsetTop ${round(
              vv.offsetTop
            )} pageTop ${round(vv.pageTop)}`
          : "n/a"
      }`,
      `scroll: ${window.scrollX}, ${window.scrollY}`,
      `100svh ${round(svh)} / 100lvh ${round(lvh)} / 100dvh ${round(dvh)}`,
      ``,
      `env top/bottom: ${envTop} / ${envBottom}`,
      `plugin top/bottom: ${rootStyle
        .getPropertyValue("--computed-safe-area-top")
        .trim()} / ${rootStyle
        .getPropertyValue("--computed-safe-area-bottom")
        .trim()}`,
      `app-height: ${rootStyle
        .getPropertyValue("--app-height")
        .trim()}  gap top/bottom: ${rootStyle
        .getPropertyValue("--viewport-gap-top")
        .trim()} / ${rootStyle
        .getPropertyValue("--viewport-gap-bottom")
        .trim()}`,
      ``,
      `html: ${rect("html")}`,
      `body: ${rect("body")}`,
      `#shell: ${rect("#shell")}`,
      `header: ${rect("app-header")}`,
      `footer: ${rect("app-footer")}`,
    ].join("\n");
  }

  onBadgeImageError(): void {
    this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
  }

  ngOnDestroy() {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
    if (this.activatedSubscription) {
      this.activatedSubscription.unsubscribe();
    }
  }

  goBack() {
    this.location.back();
  }

  isBackVisible(): boolean {
    //const id: Observable<string> = route.params.pipe(map(p => p.id));
    //const url: Observable<string> = route.url.pipe(map(segments => segments.join('')));
    return this.router.url !== "/" && this.router.url !== "/home";
  }

  isMenuVisible(): boolean {
    //const id: Observable<string> = route.params.pipe(map(p => p.id));
    //const url: Observable<string> = route.url.pipe(map(segments => segments.join('')));
    return true;
  }

  // @HostListener("window:mouseup", ["$event.target.id"])
  // onMouseDown(id: string) {
  //   var sideBarVisible =
  //     this.sideNavigatorService.getToggleClass() === "sidebar-visible";
  //   if (sideBarVisible && id != "sidebar" && id != "menu") {
  //     this.sideNavigatorService.toggleSideNavigator();
  //   }
  // }

  // @HostListener("window:touchend", ["$event.target.id"])
  // onTouchStart(id: string) {
  //   var sideBarVisible =
  //     this.sideNavigatorService.getToggleClass() === "sidebar-visible";
  //   if (sideBarVisible && id != "sidebar" && id != "menu") {
  //     this.sideNavigatorService.toggleSideNavigator();
  //   }
  // }
}
